import type { RouteRecordRaw } from 'vue-router'

// Auth entry-funnel routes (contract 06 §11), composed into app/router via the barrel. The
// `guestOnly` gate + the two-column AuthLayout brand shell + layout selection are wired by
// module 05; here the routes carry `meta.layout: 'auth'`. Built screens registered so far:
// Login + MFA + SignUpMfa + Signup + Shopify register + Forgot / Reset / TeamJoin /
// InviteAccessed / InviteExpired + SetPlan. SetPlan publishes module 11's needs-plan signal and
// never renders its own pricing implementation.
export const authRoutes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('./screens/LoginScreen.vue'),
    meta: {
      layout: 'auth',
      requiresAuth: false,
      guestOnly: true,
      titleKey: 'auth.login.title',
    },
  },
  {
    path: '/signup',
    name: 'Signup',
    component: () => import('./screens/SignupScreen.vue'),
    meta: {
      layout: 'auth',
      requiresAuth: false,
      guestOnly: true,
      titleKey: 'auth.signup.title',
    },
  },
  {
    path: '/shopify-register',
    name: 'ShopifyRegister',
    component: () => import('./screens/ShopifyRegisterScreen.vue'),
    meta: {
      layout: 'auth',
      requiresAuth: false,
      guestOnly: true,
      authLogo: false,
      titleKey: 'auth.signup.title',
    },
  },
  {
    path: '/mfa',
    name: 'MfaCode',
    component: () => import('./screens/MfaScreen.vue'),
    meta: {
      layout: 'auth',
      authLogo: false,
      requiresAuth: false,
      guestOnly: true,
      titleKey: 'auth.mfa.title',
    },
  },
  {
    path: '/signup-mfa',
    name: 'SignUpMfa',
    component: () => import('./screens/SignupMfaScreen.vue'),
    meta: { layout: 'auth', requiresAuth: false, guestOnly: true, titleKey: 'auth.mfa.title' },
  },
  {
    path: '/forgot-password',
    name: 'ForgotPassword',
    component: () => import('./screens/ForgotPasswordScreen.vue'),
    meta: {
      layout: 'auth',
      requiresAuth: false,
      guestOnly: true,
      titleKey: 'auth.forgot.title',
    },
  },
  {
    path: '/change-password',
    name: 'ResetPassword',
    component: () => import('./screens/ResetPasswordScreen.vue'),
    meta: {
      layout: 'auth',
      requiresAuth: false,
      guestOnly: true,
      titleKey: 'auth.reset.title',
    },
  },
  // Reached WITH an invite context (not guest-only): the "link already accessed" landing.
  {
    path: '/check-permission',
    name: 'CheckPermission',
    component: () => import('./screens/TeamJoinScreen.vue'),
    meta: {
      layout: 'auth',
      requiresAuth: false,
      // `mcpAllowed` (ORP-21656): the invite landing itself decides where the user goes next, so
      // the guard must let it run instead of bouncing an MCP-only invitee mid-acceptance.
      mcpAllowed: true,
      titleKey: 'auth.teamJoin.title',
    },
  },
  {
    path: '/invite-accessed',
    name: 'InviteAccessed',
    component: () => import('./screens/InviteAccessedScreen.vue'),
    meta: {
      layout: 'auth',
      authSurface: 'standalone',
      mcpAllowed: true,
    },
  },
  {
    path: '/invite-expired',
    name: 'InviteExpired',
    component: () => import('./screens/InviteExpiredScreen.vue'),
    meta: {
      layout: 'auth',
      requiresAuth: false,
      authSurface: 'standalone',
      mcpAllowed: true,
    },
  },
  {
    path: '/set-plan',
    name: 'SetPlan',
    component: () => import('./screens/SetPlanScreen.vue'),
    meta: { layout: 'blank', requiresAuth: false },
  },
  {
    path: '/pending-invites',
    name: 'PendingInvites',
    component: () => import('./screens/PendingInvitesScreen.vue'),
    meta: {
      layout: 'auth',
      requiresAuth: true,
      mcpAllowed: true,
      titleKey: 'auth.pendingInvites.title',
    },
  },
]
